import React from "react";
import {
  Box,
  useMediaQuery,
  useTheme,
  type IconButtonProps,
} from "@mui/material";
import { WaveSurferMicVisualizer } from "../../live-speech-stage/wavesurfer-visualizers/wavesurfer-visualizers";
import { AudioInput } from "../avatar-session/audio-input";
import { RoleplayCallControls } from "../../live-speech-stage/roleplay-call-controls/roleplay-call-controls";
import { getRoleplayCallMicDimensions } from "../../live-speech-stage/styles";
import {
  dockedControlsStyles,
  getAvatarControlsContainerSx,
  inVideoWaveOverlayStyles,
  waveSurferStyles,
} from "./styles";

type PushToTalkHandlers = Pick<
  IconButtonProps,
  "onPointerDown" | "onPointerUp" | "onPointerLeave" | "onPointerCancel"
>;

interface AvatarControlsProps {
  isConnected: boolean;
  showCountdown: boolean;
  isRecording: boolean;
  isMuted: boolean;
  isMicDisabledByWarning: boolean;
  isVoiceChatLoading: boolean;
  muteInputAudio: () => void;
  unmuteInputAudio: () => void;
  isPushToTalk?: boolean;
  isPushPressed?: boolean;
  mediaStream?: MediaStream | null;
  pushButtonRef?: React.Ref<HTMLButtonElement>;
  pushToTalkHandlers?: PushToTalkHandlers;
  onEndCall?: () => void;
  // Place controls under the video instead of overlaying the avatar.
  docked?: boolean;
  // When docked: render only the in-video wave overlay (parent places it
  // inside the rounded video frame).
  waveOverlayOnly?: boolean;
  // When docked: hide waves here (they are rendered via waveOverlayOnly).
  hideWave?: boolean;
}

export const AvatarControls: React.FC<AvatarControlsProps> = ({
  isConnected,
  showCountdown,
  isRecording,
  isMuted,
  isMicDisabledByWarning,
  isVoiceChatLoading,
  muteInputAudio,
  unmuteInputAudio,
  isPushToTalk = false,
  isPushPressed = false,
  mediaStream = null,
  pushButtonRef,
  pushToTalkHandlers,
  onEndCall,
  docked = false,
  waveOverlayOnly = false,
  hideWave = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { buttonPx, iconPx } = getRoleplayCallMicDimensions(isMobile, isMdDown);

  if (!isConnected || showCountdown) {
    return null;
  }

  const micActive = isPushToTalk ? isPushPressed : !isMuted;
  const isRoleplayLayout = Boolean(isPushToTalk && onEndCall);
  const showWave =
    !hideWave && isRecording && micActive && !isMicDisabledByWarning;

  if (waveOverlayOnly) {
    if (!showWave) return null;
    return (
      <Box sx={inVideoWaveOverlayStyles}>
        <WaveSurferMicVisualizer
          isRecording={true}
          height={isMobile ? 32 : 40}
          borderRadiusPx={isMobile ? 8 : 12}
        />
      </Box>
    );
  }

  const wave =
    showWave && !docked ? (
      <Box sx={waveSurferStyles}>
        <WaveSurferMicVisualizer
          isRecording={true}
          height={48}
          borderRadiusPx={isMobile ? 8 : 12}
        />
      </Box>
    ) : null;

  const controls = (
    <Box sx={getAvatarControlsContainerSx(isRoleplayLayout, docked)}>
      {isRoleplayLayout ? (
        <Box sx={{ width: "100%" }}>
          <RoleplayCallControls
            buttonPx={buttonPx}
            iconPx={iconPx}
            isPushPressed={isPushPressed}
            onEndCall={onEndCall!}
            mediaStream={mediaStream}
            pushButtonRef={pushButtonRef}
            pushToTalkHandlers={pushToTalkHandlers}
            isDisabled={isMicDisabledByWarning}
            isLoading={isVoiceChatLoading}
            layout="video"
          />
        </Box>
      ) : (
        <AudioInput
          muteInputAudio={muteInputAudio}
          unmuteInputAudio={unmuteInputAudio}
          isMuted={isMuted}
          isVoiceChatLoading={isVoiceChatLoading}
          isMicDisabledByWarning={isMicDisabledByWarning}
          isPushToTalk={isPushToTalk}
          isPushPressed={isPushPressed}
          pushButtonRef={pushButtonRef}
          pushToTalkHandlers={pushToTalkHandlers}
        />
      )}
    </Box>
  );

  if (docked) {
    return <Box sx={dockedControlsStyles}>{controls}</Box>;
  }

  return (
    <>
      {wave}
      {controls}
    </>
  );
};
